import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, addDays, eachDayOfInterval, differenceInDays } from "date-fns";
import { CalendarIcon, Users, Minus, Plus, Search, AlertCircle, BedDouble, PawPrint } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface SearchFilters {
  checkIn: Date | undefined;
  checkOut: Date | undefined;
  adults: number;
  children: number;
  bedrooms: number;
  petFriendly: boolean;
}

interface AvailabilitySearchProps {
  onSearch: (filters: SearchFilters) => void;
  isSearching?: boolean;
}

const AvailabilitySearch = ({ onSearch, isSearching }: AvailabilitySearchProps) => {
  const [checkIn, setCheckIn] = useState<Date>();
  const [checkOut, setCheckOut] = useState<Date>();
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);
  const [hoveredDate, setHoveredDate] = useState<Date>();
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [bedrooms, setBedrooms] = useState(0);
  const [petFriendly, setPetFriendly] = useState(false);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Fetch the lowest min_nights across all active properties.
  // Per-night Hostaway `minimum_stay` overrides are evaluated on the
  // property detail page where the specific listing is known.
  const { data: globalMinNights = 1 } = useQuery({
    queryKey: ["global-min-nights"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("properties")
        .select("min_nights")
        .eq("is_active", true);
      if (error || !data?.length) return 1;
      const mins = data.map(p => p.min_nights ?? 1).filter(n => n > 0);
      return mins.length > 0 ? Math.min(...mins) : 1;
    },
  });

  const handleSearch = () => {
    onSearch({ checkIn, checkOut, adults, children, bedrooms, petFriendly });
  };

  const handleClear = () => {
    setCheckIn(undefined);
    setCheckOut(undefined);
    setAdults(2);
    setChildren(0);
    setBedrooms(0);
    setPetFriendly(false);
    onSearch({ checkIn: undefined, checkOut: undefined, adults: 0, children: 0, bedrooms: 0, petFriendly: false });
  };

  const hasFilters = checkIn || checkOut || adults !== 2 || children !== 0 || bedrooms !== 0 || petFriendly;

  return (
    <div className="bg-card rounded-2xl shadow-elevated p-4 md:p-6 border border-border">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
        {/* Check-in */}
        <div className="space-y-2">
          <label className="text-xs font-body font-medium uppercase tracking-wider text-muted-foreground">
            Check-in
          </label>
          <Popover open={checkInOpen} onOpenChange={setCheckInOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-body rounded-xl h-12 border-border",
                  !checkIn && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4 text-accent" />
                {checkIn ? format(checkIn, "MMM d, yyyy") : "Select date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={checkIn}
                onSelect={(date) => {
                  setCheckIn(date);
                  if (date && checkOut && date >= checkOut) {
                    setCheckOut(undefined);
                  }
                  setCheckInOpen(false);
                  setTimeout(() => setCheckOutOpen(true), 150);
                }}
                modifiers={checkIn && checkOut ? {
                  range_start: checkIn,
                  range_middle: differenceInDays(checkOut, checkIn) > 1
                    ? eachDayOfInterval({ start: addDays(checkIn, 1), end: addDays(checkOut, -1) })
                    : [],
                  range_end: checkOut,
                } : undefined}
                modifiersClassNames={{
                  range_start: "bg-primary text-primary-foreground rounded-l-md",
                  range_middle: "bg-accent/40 text-accent-foreground",
                  range_end: "bg-primary text-primary-foreground rounded-r-md",
                }}
                disabled={(date) => date < today}
                initialFocus
                className={cn("p-3 pointer-events-auto")}
              />
              {checkIn && checkOut && (
                <div className="px-3 pb-3 pt-1 text-center">
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-body font-semibold text-primary">
                    🌙 {differenceInDays(checkOut, checkIn)} night{differenceInDays(checkOut, checkIn) !== 1 ? "s" : ""}
                  </span>
                </div>
              )}
            </PopoverContent>
          </Popover>
        </div>

        {/* Check-out */}
        <div className="space-y-2">
          <label className="text-xs font-body font-medium uppercase tracking-wider text-muted-foreground">
            Check-out
          </label>
          <Popover open={checkOutOpen} onOpenChange={setCheckOutOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-body rounded-xl h-12 border-border",
                  !checkOut && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4 text-accent" />
                {checkOut ? format(checkOut, "MMM d, yyyy") : "Select date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={checkOut}
                onSelect={(date) => {
                  setCheckOut(date);
                  setHoveredDate(undefined);
                  setCheckOutOpen(false);
                }}
                onDayMouseEnter={(day) => {
                  if (checkIn && !checkOut && day > checkIn) {
                    setHoveredDate(day);
                  }
                }}
                onDayMouseLeave={() => setHoveredDate(undefined)}
                modifiers={{
                  range_start: checkIn || undefined,
                  range_middle: (() => {
                    const endDate = checkOut || hoveredDate;
                    if (checkIn && endDate && differenceInDays(endDate, checkIn) > 1) {
                      return eachDayOfInterval({ start: addDays(checkIn, 1), end: addDays(endDate, -1) });
                    }
                    return [];
                  })(),
                  range_end: checkOut || hoveredDate || undefined,
                }}
                modifiersClassNames={{
                  range_start: "bg-primary text-primary-foreground rounded-l-md",
                  range_middle: "bg-accent/40 text-accent-foreground",
                  range_end: "bg-primary text-primary-foreground rounded-r-md",
                }}
                disabled={(date) => {
                  if (date < today) return true;
                  if (checkIn && date <= checkIn) return true;
                  if (checkIn && globalMinNights > 1 && differenceInDays(date, checkIn) < globalMinNights) return true;
                  return false;
                }}
                defaultMonth={checkIn || undefined}
                initialFocus
                className={cn("p-3 pointer-events-auto")}
              />
              {globalMinNights > 1 && (
                <p className="px-3 pb-1 pt-2 text-xs font-body text-muted-foreground text-center">
                  Minimum stay: {globalMinNights} night{globalMinNights !== 1 ? "s" : ""}
                </p>
              )}
              {checkIn && (checkOut || hoveredDate) && (() => {
                const endDate = (checkOut || hoveredDate)!;
                const nightCount = differenceInDays(endDate, checkIn);
                const belowMin = globalMinNights > 1 && nightCount < globalMinNights;
                return (
                  <div className="px-3 pb-3 pt-1 text-center space-y-1">
                    <span className={cn(
                      "inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-body font-semibold",
                      belowMin ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary"
                    )}>
                      {belowMin ? (
                        <>
                          <AlertCircle className="h-3 w-3" />
                          {nightCount} night{nightCount !== 1 ? "s" : ""} — min {globalMinNights} nights
                        </>
                      ) : (
                        <>🌙 {nightCount} night{nightCount !== 1 ? "s" : ""}</>
                      )}
                    </span>
                  </div>
                );
              })()}
            </PopoverContent>
          </Popover>
        </div>

        {/* Guests */}
        <div className="space-y-2">
          <label className="text-xs font-body font-medium uppercase tracking-wider text-muted-foreground">
            Guests
          </label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-body rounded-xl h-12 border-border"
              >
                <Users className="mr-2 h-4 w-4 text-accent" />
                {adults} Adult{adults !== 1 ? "s" : ""}
                {children > 0 && `, ${children} Child${children !== 1 ? "ren" : ""}`}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-4" align="start">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-body font-medium text-foreground text-sm">Adults</p>
                    <p className="font-body text-xs text-muted-foreground">Ages 13+</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 rounded-full"
                      onClick={() => setAdults(Math.max(1, adults - 1))}
                      disabled={adults <= 1}
                      aria-label="Decrease adults"
                    >
                      <Minus className="h-3 w-3" aria-hidden="true" />
                    </Button>
                    <span className="font-body font-medium w-4 text-center" aria-live="polite">{adults}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 rounded-full"
                      onClick={() => setAdults(adults + 1)}
                      aria-label="Increase adults"
                    >
                      <Plus className="h-3 w-3" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-body font-medium text-foreground text-sm">Children</p>
                    <p className="font-body text-xs text-muted-foreground">Ages 0–12</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 rounded-full"
                      onClick={() => setChildren(Math.max(0, children - 1))}
                      disabled={children <= 0}
                      aria-label="Decrease children"
                    >
                      <Minus className="h-3 w-3" aria-hidden="true" />
                    </Button>
                    <span className="font-body font-medium w-4 text-center" aria-live="polite">{children}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 rounded-full"
                      onClick={() => setChildren(children + 1)}
                      aria-label="Increase children"
                    >
                      <Plus className="h-3 w-3" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Bedrooms */}
        <div className="space-y-2">
          <label className="text-xs font-body font-medium uppercase tracking-wider text-muted-foreground">
            Bedrooms
          </label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-body rounded-xl h-12 border-border"
              >
                <BedDouble className="mr-2 h-4 w-4 text-accent" />
                {bedrooms === 0 ? "Any" : `${bedrooms}+ Bedroom${bedrooms !== 1 ? "s" : ""}`}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-4" align="start">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-body font-medium text-foreground text-sm">Bedrooms</p>
                  <p className="font-body text-xs text-muted-foreground">Minimum count</p>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-full"
                    onClick={() => setBedrooms(Math.max(0, bedrooms - 1))}
                    disabled={bedrooms <= 0}
                    aria-label="Decrease minimum bedrooms"
                  >
                    <Minus className="h-3 w-3" aria-hidden="true" />
                  </Button>
                  <span className="font-body font-medium w-6 text-center" aria-live="polite">
                    {bedrooms === 0 ? "Any" : `${bedrooms}+`}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-full"
                    onClick={() => setBedrooms(bedrooms + 1)}
                    aria-label="Increase minimum bedrooms"
                  >
                    <Plus className="h-3 w-3" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Search Button */}
        <div className="flex gap-2">
          <Button
            onClick={handleSearch}
            disabled={isSearching}
            className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90 font-body rounded-xl h-12 text-base"
          >
            <Search className="mr-2 h-4 w-4" />
            {isSearching ? "Searching..." : "Search"}
          </Button>
          {hasFilters && (
            <Button
              variant="ghost"
              onClick={handleClear}
              className="font-body rounded-xl h-12 text-muted-foreground"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Pet-friendly toggle */}
      <div className="mt-4 flex items-center gap-2">
        <Checkbox
          id="pet-friendly"
          checked={petFriendly}
          onCheckedChange={(checked) => setPetFriendly(checked === true)}
        />
        <label
          htmlFor="pet-friendly"
          className="flex items-center gap-1.5 font-body text-sm text-foreground cursor-pointer select-none"
        >
          <PawPrint className="h-4 w-4 text-accent" aria-hidden="true" />
          Pet friendly
        </label>
      </div>
    </div>
  );
};

export default AvailabilitySearch;
